import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { 
  HiTrash, 
  HiPause, 
  HiCheck, 
  HiX, 
  HiPlay,
  HiCurrencyDollar,
  HiCash,
  HiCube,
  HiCursorClick,
  HiArrowUp,
  HiArrowDown,
  HiClock,
  HiStop
} from 'react-icons/hi';
import apiClient from '../api/client';
import ModalOverlay from '../components/ModalOverlay';
import { useNotification } from '../context/NotificationContext';
import { computeStockResourceFactor, formatStockFactorPercent } from '../utils/stockResourceFactor';
import { formatAdPlacementLabel } from '../utils/humanLabels';
import { patchCampaignWithStrategySettings } from '../utils/bidderSettingsPatch';
import StrategySettingsForm, {
  STRATEGY_SETTINGS_DEFAULTS,
  strategySettingsFromApi,
  strategySettingsToPayload,
} from '../components/StrategySettingsForm';

const statusMap = {
  '-1': { label: 'Удалена', color: 'gray', icon: HiTrash },
  '4': { label: 'Готова к запуску', color: 'blue', icon: HiPause },
  '7': { label: 'Завершена', color: 'gray', icon: HiCheck },
  '8': { label: 'Отменена', color: 'orange', icon: HiX },
  '9': { label: 'Активна', color: 'green', icon: HiPlay },
  '11': { label: 'На паузе', color: 'orange', icon: HiPause },
};

const typeMap = {
  '4': 'В каталоге',
  '5': 'В карточке товара',
  '6': 'В поиске',
  '7': 'В рекомендациях',
  '8': 'Единая ставка',
};

function formatCampaignBidProfile(campaign) {
  if (!campaign) return '—';
  const bidTypeRaw = campaign.bid_type ?? campaign.bidType;
  let bidType = bidTypeRaw ? String(bidTypeRaw).toLowerCase() : null;
  if (!bidType && (campaign.type === 8 || campaign.campaign_type === 8)) bidType = 'unified';
  if (!bidType && (campaign.type === 9 || campaign.campaign_type === 9)) bidType = 'manual';

  const payment = String(campaign.payment_type || campaign.paymentType || '').toLowerCase();
  const placement = String(campaign.ad_placement || campaign.adPlacement || '').toLowerCase();

  const parts = [];
  if (bidType === 'unified') parts.push('Единая ставка');
  else if (bidType === 'manual') parts.push('Ручная ставка');
  if (payment === 'cpm') parts.push('CPM');
  else if (payment === 'cpc') parts.push('CPC');
  if (placement === 'combined') parts.push('поиск + рекомендации');
  else if (placement === 'search') parts.push('поиск');
  else if (placement === 'recommendations') parts.push('рекомендации');
  if (bidType === 'manual' && payment === 'cpm' && (placement === 'search' || placement === 'combined')) {
    parts.push('кластеры поиска');
  }
  return parts.length ? parts.join(' · ') : '—';
}

function isManualCampaign(campaign) {
  if (!campaign) return false;
  const bt = String(campaign.bid_type ?? campaign.bidType ?? '').toLowerCase();
  if (bt === 'manual' || bt === '1') return true;
  if (bt === 'unified' || bt === '2') return false;
  return campaign.type === 9 || campaign.campaign_type === 9;
}

function placementsFromCampaign(campaign) {
  if (campaign?.placements && typeof campaign.placements === 'object') {
    return {
      search: Boolean(campaign.placements.search),
      recommendations: Boolean(campaign.placements.recommendations),
    };
  }
  const p = String(campaign?.ad_placement || campaign?.adPlacement || '').toLowerCase();
  if (p === 'combined') return { search: true, recommendations: true };
  if (p === 'recommendations') return { search: false, recommendations: true };
  if (p === 'search') return { search: true, recommendations: false };
  return { search: true, recommendations: false };
}

function StrategyModal({ campaign, isOpen, onClose, onSave, clientId }) {
  const { notify } = useNotification();
  const [values, setValues] = useState(STRATEGY_SETTINGS_DEFAULTS);
  const [loading, setLoading] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [placementSearch, setPlacementSearch] = useState(true);
  const [placementRecommendations, setPlacementRecommendations] = useState(false);
  const [savingPlacements, setSavingPlacements] = useState(false);
  const [placementMessage, setPlacementMessage] = useState('');

  const manualCampaign = isManualCampaign(campaign);

  const loadSettings = useCallback(async () => {
    if (!campaign) return;
    const campaignId = campaign.id || campaign.campaign_id || campaign.advertId;
    if (!campaignId) return;
    setLoadingSettings(true);
    try {
      const params = clientId ? { client_id: clientId } : {};
      const { data } = await apiClient.get(`/bidder/campaign/${campaignId}/settings`, { params });
      setValues(strategySettingsFromApi(data));
    } catch (err) {
      console.error('Ошибка загрузки настроек:', err);
    } finally {
      setLoadingSettings(false);
    }
  }, [campaign, clientId]);

  useEffect(() => {
    if (campaign && isOpen) {
      loadSettings();
      const pl = placementsFromCampaign(campaign);
      setPlacementSearch(pl.search);
      setPlacementRecommendations(pl.recommendations);
      setPlacementMessage('');
    }
  }, [campaign, isOpen, loadSettings]);


  if (!isOpen || !campaign) return null;

  const handleSavePlacements = async () => {
    if (!placementSearch && !placementRecommendations) {
      notify('Нужно включить хотя бы одну зону: поиск или рекомендации', 'error');
      return;
    }
    const advertId = campaign.advert_id || campaign.advertId || campaign.id;
    if (!advertId || !clientId) return;

    setSavingPlacements(true);
    setPlacementMessage('');
    try {
      const { data } = await apiClient.post(`/campaigns/${advertId}/placements`, {
        client_id: clientId,
        search: placementSearch,
        recommendations: placementRecommendations,
      });
      const zoneLabel = formatAdPlacementLabel(data.ad_placement) || 'сохранено';
      setPlacementMessage(`Зоны обновлены: ${zoneLabel}`);
      campaign.ad_placement = data.ad_placement;
      campaign.placements = data.placements;
    } catch (err) {
      console.error('Ошибка обновления зон:', err);
      setPlacementMessage(err.response?.data?.message || 'Не удалось обновить зоны показа');
    } finally {
      setSavingPlacements(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const campaignId = campaign.id || campaign.campaign_id || campaign.advertId;
      await onSave(strategySettingsToPayload(values, campaignId));
      notify('Настройки сохранены', 'success');
      onClose();
    } catch (err) {
      console.error('Ошибка сохранения:', err);
      notify('Не удалось сохранить настройки', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalOverlay onClick={onClose}>
      <div
        className="modal-panel rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <h3 className="text-xl font-semibold mb-4 text-gray-800">Настройки стратегии</h3>
        <p className="text-sm text-gray-500 mb-3">
          {campaign.name || campaign.campaign_name || `Кампания ${campaign.advert_id || campaign.advertId}`}
        </p>
        <p className="text-sm text-indigo-700 bg-indigo-50 rounded-lg px-3 py-2 mb-4">
          Тип ставок: {formatCampaignBidProfile(campaign)}
        </p>
        {manualCampaign && (
          <div className="mb-4 p-3 rounded-lg border border-gray-200 bg-gray-50 space-y-3">
            <p className="text-sm font-medium text-gray-700">Зоны показа (ручная ставка)</p>
            <label className="flex items-center gap-2 text-gray-700 text-sm">
              <input
                type="checkbox"
                checked={placementSearch}
                onChange={(e) => setPlacementSearch(e.target.checked)}
              />
              <span>Поиск</span>
            </label>
            <label className="flex items-center gap-2 text-gray-700 text-sm">
              <input
                type="checkbox"
                checked={placementRecommendations}
                onChange={(e) => setPlacementRecommendations(e.target.checked)}
              />
              <span>Рекомендации</span>
            </label>
            <button
              type="button"
              onClick={handleSavePlacements}
              disabled={savingPlacements}
              className="glass-btn-secondary text-sm py-1.5 px-3 disabled:opacity-50"
            >
              {savingPlacements ? 'Сохранение зон...' : 'Применить зоны в WB'}
            </button>
            {placementMessage ? (
              <p className={`text-xs ${placementMessage.startsWith('Зоны обновлены') ? 'text-green-700' : 'text-red-600'}`}>
                {placementMessage}
              </p>
            ) : null}
          </div>
        )}
        {loadingSettings ? (
          <p className="text-gray-600">Загрузка...</p>
        ) : (
          <>
            <StrategySettingsForm values={values} onChange={setValues} />
            <div className="flex gap-2 mt-4">
              <button
                onClick={handleSave}
                disabled={loading}
                className="glass-btn flex-1 py-2 px-4 disabled:opacity-50"
              >
                {loading ? 'Сохранение...' : 'Сохранить'}
              </button>
              <button onClick={onClose} className="glass-btn-secondary px-4 py-2">
                Отмена
              </button>
            </div>
          </>
        )}
      </div>
    </ModalOverlay>
  );
}

// Компонент массового редактирования стратегии
function BulkStrategyModal({ campaignIds, isOpen, onClose, onSave, onUpdate, clientId }) {
  const { notify } = useNotification();
  const [values, setValues] = useState(STRATEGY_SETTINGS_DEFAULTS);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setValues(STRATEGY_SETTINGS_DEFAULTS);
    }
  }, [isOpen]);

  if (!isOpen || !campaignIds || campaignIds.length === 0) return null;

  const handleSave = async () => {
    setLoading(true);
    try {
      const promises = campaignIds.map((campaignId) =>
        onSave(strategySettingsToPayload(values, campaignId)),
      );
      await Promise.all(promises);
      notify('Настройки сохранены для выбранных кампаний', 'success');
      if (onUpdate) {
        onUpdate();
      }
      onClose();
    } catch (err) {
      console.error('Ошибка сохранения:', err);
      notify('Не удалось сохранить настройки', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalOverlay onClick={onClose}>
      <div
        className="modal-panel rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <h3 className="text-xl font-semibold mb-4 text-gray-800">Настройки стратегии</h3>
        <p className="text-sm text-gray-500 mb-2">Выбрано кампаний: {campaignIds.length}</p>
        <p className="text-sm text-amber-800 bg-amber-50 rounded-lg px-3 py-2 mb-4">
          Указанные значения будут применены ко всем выбранным кампаниям.
        </p>
        <StrategySettingsForm values={values} onChange={setValues} bulkMode />
        <div className="flex gap-2 mt-4">
          <button
            onClick={handleSave}
            disabled={loading}
            className="glass-btn flex-1 py-2 px-4 disabled:opacity-50"
          >
            {loading ? 'Сохранение...' : 'Сохранить'}
          </button>
          <button onClick={onClose} className="glass-btn-secondary px-4 py-2">
            Отмена
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

function MonthlyBudgetModal({ isOpen, onClose, clientId, onUpdate }) {
  const { notify } = useNotification();
  const [budgetData, setBudgetData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    total_budget: '',
    optimization_metric: 'acos',
    target_acos: 30,
    target_cpo: 100,
    min_daily_limit: 300,
    max_daily_limit: 50000,
    lookback_days: 7,
    auto_distribute: true,
    auto_deposit: true,
  });

  const loadBudget = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const now = new Date();
      const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      const { data } = await apiClient.get(`/budget/${clientId}`, {
        params: { month },
      });
      setBudgetData(data);
      if (data?.budget) {
        setForm({
          total_budget: data.budget.total_budget || '',
          optimization_metric: data.budget.optimization_metric || 'acos',
          target_acos: data.budget.target_acos || 30,
          target_cpo: data.budget.target_cpo || 100,
          min_daily_limit: data.budget.min_daily_limit || 300,
          max_daily_limit: data.budget.max_daily_limit || 50000,
          lookback_days: data.budget.lookback_days || 7,
          auto_distribute: data.budget.auto_distribute !== undefined ? data.budget.auto_distribute : true,
          auto_deposit: data.budget.auto_deposit !== undefined ? data.budget.auto_deposit : true,
        });
      }
    } catch (error) {
      console.error('Ошибка загрузки бюджета:', error);
      // Если бюджета нет, просто показываем форму
      setBudgetData(null);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    if (isOpen && clientId) {
      loadBudget();
    }
  }, [isOpen, clientId, loadBudget]);


  const handleSetBudget = async (e) => {
    e.preventDefault();
    if (!clientId) return;
    setSaving(true);
    try {
      const now = new Date();
      const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      const { data: postData } = await apiClient.post('/budget', {
        client_id: clientId,
        month,
        total_budget: Number(form.total_budget),
      });
      const { data: putData } = await apiClient.put(`/budget/${clientId}/settings`, {
        month,
        optimization_metric: form.optimization_metric,
        target_acos: Number(form.target_acos),
        target_cpo: Number(form.target_cpo),
        min_daily_limit: Number(form.min_daily_limit),
        max_daily_limit: Number(form.max_daily_limit),
        lookback_days: Number(form.lookback_days),
        auto_distribute: form.auto_distribute,
        auto_deposit: form.auto_deposit,
      });
      const distributionPending = Boolean(postData?.distribution_pending || putData?.distribution_pending);
      notify(
        distributionPending
          ? 'Бюджет сохранён. Распределение по кампаниям выполняется в фоне…'
          : 'Бюджет успешно сохранен',
        'success',
      );
      loadBudget();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Ошибка сохранения бюджета:', error);
      notify('Не удалось сохранить бюджет', 'error');
    } finally {
      setSaving(false);
    }
  };


  if (!isOpen) return null;

  const formatNumber = (num) => {
    if (!num && num !== 0) return '0';
    return Number(num).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <ModalOverlay onClick={onClose}>
      <div
        className="modal-panel rounded-2xl p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <h3 className="text-xl font-semibold mb-4 text-gray-800">Управление месячным бюджетом</h3>
        {loading ? (
          <p className="text-gray-600">Загрузка...</p>
        ) : (
          <div className="space-y-6">
            {/* Текущий статус бюджета */}
            {budgetData?.budget && (
              <div className="glass-card p-4">
                <h4 className="font-semibold mb-3">Текущий бюджет</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Общий бюджет</p>
                    <p className="text-lg font-semibold">{formatNumber(budgetData.budget.total_budget)} ₽</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Потрачено</p>
                    <p className="text-lg font-semibold text-orange-600">{formatNumber(budgetData.budget.spent_budget)} ₽</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Остаток</p>
                    <p className="text-lg font-semibold text-green-600">
                      {formatNumber(budgetData.budget.total_budget - budgetData.budget.spent_budget)} ₽
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Форма настройки бюджета */}
            <form onSubmit={handleSetBudget} className="space-y-4">
              <div>
                <label className="block mb-1 font-medium text-gray-700">Месячный бюджет, ₽</label>
                <input
                  type="number"
                  value={form.total_budget}
                  onChange={(e) => setForm({ ...form, total_budget: e.target.value })}
                  className="glass-input w-full"
                  required
                  min="0"
                  step="0.01"
                  placeholder="300000"
                />
              </div>

              <div>
                <label className="block mb-1 font-medium text-gray-700">Метрика оптимизации</label>
                <select
                  value={form.optimization_metric}
                  onChange={(e) => setForm({ ...form, optimization_metric: e.target.value })}
                  className="glass-input w-full"
                >
                  <option value="acos">ACoS (Advertising Cost of Sales)</option>
                  <option value="cpo">CPO (Cost Per Order)</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {form.optimization_metric === 'acos' 
                    ? 'Оптимизация по проценту расходов от выручки (чем ниже, тем лучше)'
                    : 'Оптимизация по стоимости заказа (чем ниже, тем лучше)'}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {form.optimization_metric === 'acos' ? (
                  <div>
                    <label className="block mb-1 font-medium text-gray-700">Целевой ACoS, %</label>
                    <input
                      type="number"
                      value={form.target_acos}
                      onChange={(e) => setForm({ ...form, target_acos: e.target.value })}
                      className="glass-input w-full"
                      min="0"
                      max="100"
                      step="0.1"
                    />
                  </div>
                ) : (
                  <div>
                    <label className="block mb-1 font-medium text-gray-700">Целевой CPO, ₽</label>
                    <input
                      type="number"
                      value={form.target_cpo}
                      onChange={(e) => setForm({ ...form, target_cpo: e.target.value })}
                      className="glass-input w-full"
                      min="0"
                      step="0.01"
                    />
                  </div>
                )}
                <div>
                  <label className="block mb-1 font-medium text-gray-700">Дней для анализа</label>
                  <input
                    type="number"
                    value={form.lookback_days}
                    onChange={(e) => setForm({ ...form, lookback_days: e.target.value })}
                    className="glass-input w-full"
                    min="1"
                    max="30"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block mb-1 font-medium text-gray-700">Мин. дневной лимит, ₽</label>
                  <input
                    type="number"
                    value={form.min_daily_limit}
                    onChange={(e) => setForm({ ...form, min_daily_limit: e.target.value })}
                    className="glass-input w-full"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div>
                  <label className="block mb-1 font-medium text-gray-700">Макс. дневной лимит, ₽</label>
                  <input
                    type="number"
                    value={form.max_daily_limit}
                    onChange={(e) => setForm({ ...form, max_daily_limit: e.target.value })}
                    className="glass-input w-full"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.auto_distribute}
                    onChange={(e) => setForm({ ...form, auto_distribute: e.target.checked })}
                  />
                  <span>Автоматическое распределение</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.auto_deposit}
                    onChange={(e) => setForm({ ...form, auto_deposit: e.target.checked })}
                  />
                  <span>Автоматическое пополнение бюджета кампаний (минимум 1000 ₽)</span>
                </label>
              </div>

              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={saving}
                  className="glass-btn flex-1 py-2 px-4 disabled:opacity-50"
                >
                  {saving ? 'Сохранение...' : 'Сохранить бюджет'}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="glass-btn-secondary px-4 py-2"
                >
                  Закрыть
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </ModalOverlay>
  );
}

// Компонент модального окна с графиком показов
function normalizeScheduleData(data = {}) {
  const scheduleData = {};
  for (let d = 0; d < 7; d++) {
    for (let h = 0; h < 24; h++) {
      const key = `${d}_${h}`;
      const value = data[key];
      if (value !== undefined && value !== null) {
        if (typeof value === 'number') {
          scheduleData[key] = value >= 50 ? 100 : 0;
        } else if (value === true) {
          scheduleData[key] = 100;
        } else {
          scheduleData[key] = 0;
        }
      } else {
        scheduleData[key] = 0;
      }
    }
  }
  return scheduleData;
}

function emptyScheduleGrid() {
  const grid = {};
  for (let d = 0; d < 7; d++) {
    for (let h = 0; h < 24; h++) {
      grid[`${d}_${h}`] = 0;
    }
  }
  return grid;
}

function ScheduleModal({ campaign, isOpen, onClose, clientId }) {
  const { confirm } = useNotification();
  const [schedule, setSchedule] = useState({});
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [heatmapLoading, setHeatmapLoading] = useState(false);
  const days = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

  useEffect(() => {
    if (!campaign || !clientId || !isOpen) {
      setSchedule(emptyScheduleGrid());
      setMessage('');
      return;
    }

    const advertId = campaign.advertId || campaign.advert_id || campaign.id;
    if (!advertId) return;

    setLoading(true);
    apiClient
      .get('/schedule', {
        params: {
          advert_id: advertId,
          client_id: clientId,
        },
      })
      .then(res => {
        setSchedule(normalizeScheduleData(res.data || {}));
        setLoading(false);
      })
      .catch(err => {
        console.error('Ошибка загрузки расписания:', err);
        setSchedule(emptyScheduleGrid());
        setLoading(false);
      });
  }, [campaign, clientId, isOpen]);

  const toggleCell = (day, hour) => {
    const key = `${day}_${hour}`;
    setSchedule(prev => ({
      ...prev,
      [key]: prev[key] === 100 ? 0 : 100,
    }));
  };

  const toggleDay = (dayIndex) => {
    const updated = { ...schedule };
    const allActive = Array.from({ length: 24 }, (_, h) => `${dayIndex}_${h}`).every(k => updated[k] === 100);
    const newValue = allActive ? 0 : 100;
    for (let h = 0; h < 24; h++) {
      const key = `${dayIndex}_${h}`;
      updated[key] = newValue;
    }
    setSchedule(updated);
  };

  const toggleHour = (hourIndex) => {
    const updated = { ...schedule };
    const allActive = Array.from({ length: 7 }, (_, d) => `${d}_${hourIndex}`).every(k => updated[k] === 100);
    const newValue = allActive ? 0 : 100;
    for (let d = 0; d < 7; d++) {
      const key = `${d}_${hourIndex}`;
      updated[key] = newValue;
    }
    setSchedule(updated);
  };

  const applyHeatmapAction = async (mode) => {
    if (!campaign || !clientId) return;
    const advertId = campaign.advertId || campaign.advert_id || campaign.id;
    if (!advertId) return;

    if (mode === 'clear') {
      const ok = await confirm('Очистить режим показов? Все часы будут выключены и изменения сохранятся.');
      if (!ok) return;
    }

    setHeatmapLoading(true);
    setMessage('');
    try {
      if (mode === 'clear') {
        const res = await apiClient.post('/schedule/clear', {
          advert_id: Number(advertId),
          client_id: Number(clientId),
        });
        setSchedule(normalizeScheduleData(res.data?.schedule || {}));
        setMessage(res.data?.message || 'Режим показов очищен');
      } else {
        const res = await apiClient.post('/schedule/from-heatmap', {
          advert_id: Number(advertId),
          client_id: Number(clientId),
          mode,
          days: 30,
          apply: mode === 'auto',
        });
        setSchedule(normalizeScheduleData(res.data?.schedule || {}));
        const explanation = res.data?.explanation || '';
        if (mode === 'auto') {
          setMessage(explanation || 'Автоматический режим применён и сохранён');
        } else {
          setMessage(explanation || 'Средние по аккаунту подставлены — отредактируйте и нажмите «Сохранить»');
        }
      }
    } catch (err) {
      const apiMsg = err.response?.data?.message;
      setMessage(apiMsg || 'Ошибка при построении расписания');
      console.error('Ошибка heatmap schedule:', err);
    } finally {
      setHeatmapLoading(false);
      setTimeout(() => setMessage(''), 8000);
    }
  };

  const saveSchedule = () => {
    if (!campaign || !clientId) return;
    const advertId = campaign.advertId || campaign.advert_id || campaign.id;
    if (!advertId) return;

    // Убеждаемся, что все 168 ячеек (7 дней * 24 часа) присутствуют в schedule
    const completeSchedule = {};
    for (let d = 0; d < 7; d++) {
      for (let h = 0; h < 24; h++) {
        const key = `${d}_${h}`;
        completeSchedule[key] = schedule[key] !== undefined ? schedule[key] : 0;
      }
    }

    apiClient
      .post('/schedule', {
        advert_id: Number(advertId),
        schedule: completeSchedule,
        client_id: Number(clientId)
      })
      .then(() => {
        setMessage('Расписание сохранено');
        setTimeout(() => setMessage(''), 3000);
        // Обновляем локальное состояние после успешного сохранения
        setSchedule(completeSchedule);
      })
      .catch(err => {
        const apiMsg = err.response?.data?.message;
        setMessage(apiMsg || 'Ошибка при сохранении');
        console.error('Ошибка сохранения расписания:', err);
        setTimeout(() => setMessage(''), 5000);
      });
  };

  if (!isOpen || !campaign) return null;

  const campaignName = campaign.name || `Кампания ${campaign.advertId || campaign.advert_id || campaign.id}`;

  return (
    <ModalOverlay onClick={onClose}>
      <div
        className="modal-panel rounded-2xl max-w-5xl w-full max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {/* Заголовок */}
        <div className="modal-header flex justify-between items-center p-4 sticky top-0 z-10 rounded-t-2xl">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Режим показов</h3>
            <p className="text-xs text-gray-500 mt-0.5">{campaignName}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <HiX className="w-5 h-5" />
          </button>
        </div>

        {/* Контент */}
        <div className="p-4">
          {loading ? (
            <div className="text-center text-gray-600 py-6 text-sm">Загрузка расписания...</div>
          ) : (
            <>
              <div className="mb-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => applyHeatmapAction('auto')}
                  disabled={heatmapLoading}
                  className="glass-btn px-3 py-2 text-xs font-medium disabled:opacity-50"
                  title="Алгоритм анализирует тепловую карту кампании и включает часы, где метрика в пределах цели биддера"
                >
                  {heatmapLoading ? '…' : 'Автоматический режим'}
                </button>
                <button
                  type="button"
                  onClick={() => applyHeatmapAction('average')}
                  disabled={heatmapLoading}
                  className="glass-btn-secondary px-3 py-2 text-xs font-medium disabled:opacity-50"
                  title="Заполнить сетку средними данными по аккаунту для ручной корректировки"
                >
                  Средние по аккаунту
                </button>
                <button
                  type="button"
                  onClick={() => applyHeatmapAction('clear')}
                  disabled={heatmapLoading}
                  className="px-3 py-2 text-xs font-medium rounded-lg border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-50"
                  title="Выключить все часы и сохранить"
                >
                  Очистить
                </button>
              </div>

              <div className="overflow-x-auto mb-4 glass-card p-4">
                <div className="inline-block min-w-full">
                  <div className="flex gap-1">
                    <div className="w-12 flex-shrink-0"></div>
                    {Array.from({ length: 24 }, (_, h) => {
                      const allActive = Array.from({ length: 7 }, (_, d) => schedule[`${d}_${h}`] === 100).every(v => v);
                      return (
                        <div
                          key={h}
                          className="w-8 h-8 flex items-center justify-center bg-gray-700 text-white text-xs font-medium rounded cursor-pointer hover:bg-gray-600 transition-colors"
                          onClick={() => toggleHour(h)}
                          title={`Кликните, чтобы ${allActive ? 'выключить' : 'включить'} все часы ${h} во всех днях`}
                        >
                          {h}
                        </div>
                      );
                    })}
                  </div>
                  {days.map((day, i) => {
                    const allDayActive = Array.from({ length: 24 }, (_, h) => schedule[`${i}_${h}`] === 100).every(v => v);
                    return (
                      <div key={i} className="flex gap-1 mt-1">
                        <div
                          className="w-12 h-8 flex items-center justify-center bg-gray-700 text-white text-xs font-medium rounded cursor-pointer hover:bg-gray-600 transition-colors flex-shrink-0"
                          onClick={() => toggleDay(i)}
                          title={`Кликните, чтобы ${allDayActive ? 'выключить' : 'включить'} все часы в ${day}`}
                        >
                          {day}
                        </div>
                        {Array.from({ length: 24 }, (_, h) => {
                          const key = `${i}_${h}`;
                          const isActive = schedule[key] === 100;
                          return (
                            <div
                              key={h}
                              onClick={() => toggleCell(i, h)}
                              className={`w-8 h-8 flex items-center justify-center rounded cursor-pointer transition-all ${
                                isActive 
                                  ? 'bg-green-600 hover:bg-green-700 border border-green-700' 
                                  : 'bg-gray-200 hover:bg-gray-300 border border-gray-300'
                              }`}
                              title={`${isActive ? 'Включено' : 'Выключено'} - кликните, чтобы ${isActive ? 'выключить' : 'включить'} кампанию в ${day}, час ${h}`}
                            >
                              {isActive && <HiCheck className="w-4 h-4 text-white" />}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>

              {message && (
                <div className={`mb-3 p-2 rounded text-center text-xs ${
                  message.includes('Ошибка') || message.includes('Задайте') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
                }`}>
                  {message}
                </div>
              )}

              <div className="flex justify-end gap-2">
                <button onClick={onClose} className="glass-btn-secondary px-4 py-2 text-sm font-medium" disabled={heatmapLoading}>
                  Отмена
                </button>
                <button onClick={saveSchedule} className="glass-btn px-4 py-2 text-sm font-medium" disabled={heatmapLoading}>
                  Сохранить
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </ModalOverlay>
  );
}

export default function BidderTargets({ clientId }) {
  const { notify } = useNotification();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [budgetModalOpen, setBudgetModalOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [selectedCampaignForSchedule, setSelectedCampaignForSchedule] = useState(null);
  const [budgetInfo, setBudgetInfo] = useState(null);
  const [selectedCampaignIds, setSelectedCampaignIds] = useState(new Set());
  const [backgroundRefreshing, setBackgroundRefreshing] = useState(false);

  const loadCampaigns = useCallback(async ({ silent = false } = {}) => {
    if (!clientId) return;
    if (!silent) setLoading(true);
    try {
      const { data } = await apiClient.get('/campaigns', {
        params: { client_id: clientId },
      });
      setCampaigns(data.campaigns || []);
    } catch (error) {
      console.error('Ошибка загрузки кампаний:', error);
      if (!silent) notify('Не удалось загрузить кампании', 'error');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [clientId, notify]);

  const loadBudgetInfo = useCallback(async () => {
    if (!clientId) return;
    try {
      const now = new Date();
      const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      const { data } = await apiClient.get(`/budget/${clientId}`, {
        params: { month },
      });
      setBudgetInfo(data);
    } catch (error) {
      setBudgetInfo(null);
    }
  }, [clientId]);

  const refreshDataInBackground = useCallback(() => {
    setBackgroundRefreshing(true);
    Promise.all([
      loadCampaigns({ silent: true }),
      loadBudgetInfo(),
    ]).finally(() => setBackgroundRefreshing(false));
  }, [loadCampaigns, loadBudgetInfo]);

  const saveCampaignStrategy = useCallback(async (settings) => {
    const params = clientId ? { client_id: clientId } : {};
    await apiClient.put(`/bidder/campaign/${settings.campaign_id}/settings`, settings, { params });
    setCampaigns((prev) => prev.map((c) => patchCampaignWithStrategySettings(c, settings)));
    refreshDataInBackground();
  }, [clientId, refreshDataInBackground]);

  useEffect(() => {
    if (clientId) {
      loadCampaigns();
      loadBudgetInfo();
    }
  }, [clientId, loadCampaigns, loadBudgetInfo]);



  const handleCampaignAction = async (advertId, action) => {
    setActionLoading({ ...actionLoading, [advertId]: true });
    try {
      let endpoint = '';
      let method = 'POST';
      
      switch (action) {
        case 'start':
          endpoint = `/campaigns/${advertId}/start`;
          break;
        case 'stop':
          endpoint = `/campaigns/${advertId}/stop`;
          break;
        case 'pause':
          endpoint = `/campaigns/${advertId}/pause`;
          break;
        case 'delete':
          endpoint = `/campaigns/${advertId}`;
          method = 'DELETE';
          break;
        default:
          return;
      }

      if (method === 'DELETE') {
        await apiClient.delete(endpoint, {
          params: { client_id: clientId },
        });
      } else {
        await apiClient.post(endpoint, { client_id: clientId });
      }

      // Обновляем список в фоне
      refreshDataInBackground();
    } catch (error) {
      console.error(`Ошибка ${action}:`, error);
      notify(`Не удалось выполнить действие: ${error.response?.data?.message || error.message}`, 'error');
    } finally {
      setActionLoading({ ...actionLoading, [advertId]: false });
    }
  };

  const formatNumber = (num) => {
    if (!num && num !== 0) return '0';
    return Number(num).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatBudget = (budget) => {
    if (!budget) return '—';
    return `${formatNumber(budget)} ₽`;
  };

  const getStatusBadge = (status) => {
    const statusInfo = statusMap[String(status)] || { label: 'Неизвестно', color: 'gray', icon: HiX };
    const IconComponent = statusInfo.icon;
    const colorClasses = {
      green: 'bg-green-100 text-green-800',
      orange: 'bg-orange-100 text-orange-800',
      blue: 'bg-blue-100 text-blue-800',
      gray: 'bg-gray-100 text-gray-800',
    };
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium flex items-center gap-1 ${colorClasses[statusInfo.color]}`}>
        <IconComponent className="w-3 h-3" />
        {statusInfo.label}
      </span>
    );
  };

  const getStrategyLabel = (campaign) => {
    if (!campaign.bidder_enabled) {
      return 'Биддер выключен';
    }
    
    if (campaign.strategy_type === 'target_cpc' && campaign.target_cpc) {
      return `По стоимости клика: ${formatNumber(campaign.target_cpc)} ₽`;
    }
    if (campaign.strategy_type === 'conversion_cost' && campaign.target_conversion_cost) {
      return `По стоимости конверсии: ${formatNumber(campaign.target_conversion_cost)} ₽`;
    }
    if (campaign.strategy_type === 'threshold_scout') {
      return campaign.target_conversion_cost
        ? `Скаут порога: CPO ${formatNumber(campaign.target_conversion_cost)} ₽`
        : 'Скаут порога';
    }
    if (campaign.strategy_type === 'drr' && campaign.target_drr) {
      return `По ДРР: ${formatNumber(campaign.target_drr)}%`;
    }
    
    return 'Стратегия не настроена';
  };

  const totalSpend = campaigns.reduce((sum, c) => sum + (c.spend || 0), 0);
  const totalSpendWithVat = campaigns.reduce((sum, c) => sum + (c.spend_with_vat || 0), 0);
  const totalViews = campaigns.reduce((sum, c) => sum + (c.views || 0), 0);
  const totalClicks = campaigns.reduce((sum, c) => sum + (c.clicks || 0), 0);
  const totalOrders = campaigns.reduce((sum, c) => sum + (c.orders || 0), 0);
  const totalRevenue = campaigns.reduce((sum, c) => sum + (c.revenue || 0), 0);
  const totalWeeklyBudget = campaigns.reduce((sum, c) => sum + (c.weeklyBudget || 0), 0);
  const totalCpo = totalOrders > 0 ? totalSpend / totalOrders : null;
  const totalDrr = totalRevenue > 0 ? (totalSpend / totalRevenue * 100) : null; // ДРР = (расход / доход) * 100%
  const totalCtr = totalViews > 0 ? (totalClicks / totalViews) * 100 : 0;
  const activeCampaigns = campaigns.filter(c => c.status === 9).length;

  // Состояния для сортировки
  const [sortColumn, setSortColumn] = useState(null); // 'spend', 'views', 'clicks', 'orders', 'cpo', 'revenue', 'drr', 'ctr'
  const [sortDirection, setSortDirection] = useState(null); // 'desc', 'asc', null

  const handleSort = (column) => {
    if (sortColumn !== column) {
      // Первый клик - сортировка по убыванию
      setSortColumn(column);
      setSortDirection('desc');
    } else if (sortDirection === 'desc') {
      // Второй клик - сортировка по возрастанию
      setSortDirection('asc');
    } else if (sortDirection === 'asc') {
      // Третий клик - убрать сортировку
      setSortColumn(null);
      setSortDirection(null);
    }
  };

  const sortedCampaigns = useMemo(() => {
    if (!sortColumn || !sortDirection) return campaigns;
    
    const sorted = [...campaigns];
    return sorted.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortColumn) {
        case 'spend':
          aValue = a.spend || 0;
          bValue = b.spend || 0;
          break;
        case 'views':
          aValue = a.views || 0;
          bValue = b.views || 0;
          break;
        case 'clicks':
          aValue = a.clicks || 0;
          bValue = b.clicks || 0;
          break;
        case 'ctr':
          aValue = a.views > 0 ? (a.clicks / a.views) * 100 : 0;
          bValue = b.views > 0 ? (b.clicks / b.views) * 100 : 0;
          break;
        case 'orders':
          aValue = a.orders || 0;
          bValue = b.orders || 0;
          break;
        case 'cpo':
          aValue = a.cpo !== null ? a.cpo : 0;
          bValue = b.cpo !== null ? b.cpo : 0;
          break;
        case 'revenue':
          aValue = a.revenue || 0;
          bValue = b.revenue || 0;
          break;
        case 'drr':
          aValue = a.drr !== null ? a.drr : 0;
          bValue = b.drr !== null ? b.drr : 0;
          break;
        case 'stock_count':
          aValue = a.stock_count != null ? Number(a.stock_count) : -1;
          bValue = b.stock_count != null ? Number(b.stock_count) : -1;
          break;
        case 'stock_factor':
          aValue = computeStockResourceFactor(a.stock_count);
          bValue = computeStockResourceFactor(b.stock_count);
          break;
        default:
          return 0;
      }
      
      if (sortDirection === 'desc') {
        return bValue - aValue;
      } else {
        return aValue - bValue;
      }
    });
  }, [campaigns, sortColumn, sortDirection]);

  return (
    <div className="space-y-6">
      {/* Заголовок */}
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Биддер ставок</h2>
          <p className="text-sm text-gray-500 mt-1">
            Управление кампаниями и стратегиями
            {backgroundRefreshing && (
              <span className="ml-2 text-indigo-600">· обновление данных…</span>
            )}
          </p>
        </div>
        <button
          onClick={() => setBudgetModalOpen(true)}
          className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-medium py-2.5 px-6 rounded-lg shadow-md transition-all duration-200 flex items-center gap-2"
        >
          <HiCurrencyDollar className="w-5 h-5" />
          <span>Месячный бюджет</span>
        </button>
      </div>

      {/* Блок информации о месячном бюджете */}
      {budgetInfo?.budget && (
        <>
          <div className="grid grid-cols-4 gap-3">
            {/* Общий бюджет */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium text-gray-600">Общий бюджет</p>
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <HiCurrencyDollar className="w-4 h-4 text-blue-600" />
                </div>
              </div>
              <p className="text-lg font-bold text-gray-900">{formatNumber(budgetInfo.budget.total_budget)} ₽</p>
              <button
                onClick={() => setBudgetModalOpen(true)}
                className="text-xs text-blue-600 hover:text-blue-700 font-medium mt-1"
              >
                Редактировать →
              </button>
            </div>

            {/* Потрачено */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium text-gray-600">Потрачено</p>
                <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                  <HiCash className="w-4 h-4 text-orange-600" />
                </div>
              </div>
              <p className="text-lg font-bold text-gray-900">{formatNumber(budgetInfo.budget.spent_budget)} ₽</p>
            </div>

            {/* Остаток */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium text-gray-600">Остаток</p>
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                  (budgetInfo.budget.total_budget - budgetInfo.budget.spent_budget) < 0 ? 'bg-red-100' : 'bg-green-100'
                }`}>
                  {(budgetInfo.budget.total_budget - budgetInfo.budget.spent_budget) < 0 ? (
                    <HiArrowDown className="w-4 h-4 text-red-600" />
                  ) : (
                    <HiArrowUp className="w-4 h-4 text-green-600" />
                  )}
                </div>
              </div>
              <p className={`text-lg font-bold ${
                (budgetInfo.budget.total_budget - budgetInfo.budget.spent_budget) < 0 ? 'text-red-600' : 'text-green-600'
              }`}>
                {formatNumber(budgetInfo.budget.total_budget - budgetInfo.budget.spent_budget)} ₽
              </p>
            </div>

            {/* Использовано */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium text-gray-600">Использовано</p>
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                  budgetInfo.budget.total_budget > 0 && (budgetInfo.budget.spent_budget / budgetInfo.budget.total_budget) * 100 > 100
                    ? 'bg-red-100'
                    : budgetInfo.budget.total_budget > 0 && (budgetInfo.budget.spent_budget / budgetInfo.budget.total_budget) * 100 > 80
                    ? 'bg-orange-100'
                    : 'bg-green-100'
                }`}>
                  <div className={`w-3 h-3 rounded-full ${
                    budgetInfo.budget.total_budget > 0 && (budgetInfo.budget.spent_budget / budgetInfo.budget.total_budget) * 100 > 100
                      ? 'bg-red-600'
                      : budgetInfo.budget.total_budget > 0 && (budgetInfo.budget.spent_budget / budgetInfo.budget.total_budget) * 100 > 80
                      ? 'bg-orange-600'
                      : 'bg-green-600'
                  }`} />
                </div>
              </div>
              <p className="text-lg font-bold text-gray-900 mb-1">
                {budgetInfo.budget.total_budget > 0
                  ? formatNumber(Math.min((budgetInfo.budget.spent_budget / budgetInfo.budget.total_budget) * 100, 100))
                  : '0'}%
              </p>
              <div className="flex-1 bg-gray-200 rounded-full h-2 mt-1">
                <div
                  className={`h-2 rounded-full ${
                    budgetInfo.budget.total_budget > 0 && (budgetInfo.budget.spent_budget / budgetInfo.budget.total_budget) * 100 > 100
                      ? 'bg-red-500'
                      : budgetInfo.budget.total_budget > 0 && (budgetInfo.budget.spent_budget / budgetInfo.budget.total_budget) * 100 > 80
                      ? 'bg-orange-500'
                      : 'bg-green-500'
                  }`}
                  style={{ 
                    width: `${Math.min(
                      budgetInfo.budget.total_budget > 0
                        ? (budgetInfo.budget.spent_budget / budgetInfo.budget.total_budget) * 100
                        : 0,
                      100
                    )}%` 
                  }}
                />
              </div>
            </div>
          </div>
        </>
      )}

      {/* Таблица кампаний */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex flex-col" style={{ maxHeight: 'calc(100vh - 300px)' }}>
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            <p className="mt-4 text-gray-500">Загрузка кампаний...</p>
          </div>
        ) : (
          <div className="overflow-x-auto overflow-y-auto flex-1">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
                  <th className="text-left p-4 text-xs font-semibold text-gray-700 uppercase tracking-wider bg-gray-50">
                    <input
                      type="checkbox"
                      checked={selectedCampaignIds.size > 0 && selectedCampaignIds.size === sortedCampaigns.length}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedCampaignIds(new Set(sortedCampaigns.map(c => c.advertId)));
                        } else {
                          setSelectedCampaignIds(new Set());
                        }
                      }}
                      className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                  </th>
                  <th className="text-left p-4 text-xs font-semibold text-gray-700 uppercase tracking-wider bg-gray-50">Тип</th>
                  <th className="text-left p-4 text-xs font-semibold text-gray-700 uppercase tracking-wider bg-gray-50">Название</th>
                  <th className="text-left p-4 text-xs font-semibold text-gray-700 uppercase tracking-wider bg-gray-50">Статус</th>
                  <th className="text-left p-4 text-xs font-semibold text-gray-700 uppercase tracking-wider bg-gray-50">Бюджет и стратегия</th>
                  <th className="text-left p-4 text-xs font-semibold text-gray-700 uppercase tracking-wider bg-gray-50">Места</th>
                  <th className="text-right p-4 text-xs font-semibold text-gray-700 uppercase tracking-wider bg-gray-50">
                    <button
                      onClick={() => handleSort('spend')}
                      className="flex items-center gap-1 hover:text-gray-900 transition-colors"
                    >
                      <span>Расход, ₽</span>
                      {sortColumn === 'spend' && (
                        sortDirection === 'desc' ? (
                          <HiArrowDown className="w-4 h-4 text-blue-600" />
                        ) : (
                          <HiArrowUp className="w-4 h-4 text-blue-600" />
                        )
                      )}
                    </button>
                  </th>
                  <th className="text-right p-4 text-xs font-semibold text-gray-700 uppercase tracking-wider bg-gray-50">
                    <button
                      onClick={() => handleSort('views')}
                      className="flex items-center gap-1 hover:text-gray-900 transition-colors"
                    >
                      <span>Показы</span>
                      {sortColumn === 'views' && (
                        sortDirection === 'desc' ? (
                          <HiArrowDown className="w-4 h-4 text-blue-600" />
                        ) : (
                          <HiArrowUp className="w-4 h-4 text-blue-600" />
                        )
                      )}
                    </button>
                  </th>
                  <th className="text-right p-4 text-xs font-semibold text-gray-700 uppercase tracking-wider bg-gray-50">
                    <button
                      onClick={() => handleSort('clicks')}
                      className="flex items-center gap-1 hover:text-gray-900 transition-colors"
                    >
                      <span>Клики</span>
                      {sortColumn === 'clicks' && (
                        sortDirection === 'desc' ? (
                          <HiArrowDown className="w-4 h-4 text-blue-600" />
                        ) : (
                          <HiArrowUp className="w-4 h-4 text-blue-600" />
                        )
                      )}
                    </button>
                  </th>
                  <th className="text-right p-4 text-xs font-semibold text-gray-700 uppercase tracking-wider bg-gray-50">
                    <button
                      onClick={() => handleSort('ctr')}
                      className="flex items-center gap-1 hover:text-gray-900 transition-colors"
                    >
                      <span>CTR, %</span>
                      {sortColumn === 'ctr' && (
                        sortDirection === 'desc' ? (
                          <HiArrowDown className="w-4 h-4 text-blue-600" />
                        ) : (
                          <HiArrowUp className="w-4 h-4 text-blue-600" />
                        )
                      )}
                    </button>
                  </th>
                  <th className="text-right p-4 text-xs font-semibold text-gray-700 uppercase tracking-wider bg-gray-50">
                    <button
                      onClick={() => handleSort('orders')}
                      className="flex items-center gap-1 hover:text-gray-900 transition-colors"
                    >
                      <span>Заказы</span>
                      {sortColumn === 'orders' && (
                        sortDirection === 'desc' ? (
                          <HiArrowDown className="w-4 h-4 text-blue-600" />
                        ) : (
                          <HiArrowUp className="w-4 h-4 text-blue-600" />
                        )
                      )}
                    </button>
                  </th>
                  <th className="text-right p-4 text-xs font-semibold text-gray-700 uppercase tracking-wider bg-gray-50">
                    <button
                      onClick={() => handleSort('cpo')}
                      className="flex items-center gap-1 hover:text-gray-900 transition-colors"
                    >
                      <span>CPO, ₽</span>
                      {sortColumn === 'cpo' && (
                        sortDirection === 'desc' ? (
                          <HiArrowDown className="w-4 h-4 text-blue-600" />
                        ) : (
                          <HiArrowUp className="w-4 h-4 text-blue-600" />
                        )
                      )}
                    </button>
                  </th>
                  <th className="text-right p-4 text-xs font-semibold text-gray-700 uppercase tracking-wider bg-gray-50">
                    <button
                      onClick={() => handleSort('revenue')}
                      className="flex items-center gap-1 hover:text-gray-900 transition-colors"
                    >
                      <span>Доход, ₽</span>
                      {sortColumn === 'revenue' && (
                        sortDirection === 'desc' ? (
                          <HiArrowDown className="w-4 h-4 text-blue-600" />
                        ) : (
                          <HiArrowUp className="w-4 h-4 text-blue-600" />
                        )
                      )}
                    </button>
                  </th>
                  <th className="text-right p-4 text-xs font-semibold text-gray-700 uppercase tracking-wider bg-gray-50">
                    <button
                      onClick={() => handleSort('drr')}
                      className="flex items-center gap-1 hover:text-gray-900 transition-colors"
                    >
                      <span>ДРР, %</span>
                      {sortColumn === 'drr' && (
                        sortDirection === 'desc' ? (
                          <HiArrowDown className="w-4 h-4 text-blue-600" />
                        ) : (
                          <HiArrowUp className="w-4 h-4 text-blue-600" />
                        )
                      )}
                    </button>
                  </th>
                  <th className="text-right p-4 text-xs font-semibold text-gray-700 uppercase tracking-wider bg-gray-50">
                    <button
                      type="button"
                      onClick={() => handleSort('stock_count')}
                      className="flex items-center gap-1 hover:text-gray-900 transition-colors ml-auto"
                      title="Остаток на WB по продвигаемому артикулу"
                    >
                      <span>Остаток, шт</span>
                      {sortColumn === 'stock_count' && (
                        sortDirection === 'desc' ? (
                          <HiArrowDown className="w-4 h-4 text-blue-600" />
                        ) : (
                          <HiArrowUp className="w-4 h-4 text-blue-600" />
                        )
                      )}
                    </button>
                  </th>
                  <th className="text-right p-4 text-xs font-semibold text-gray-700 uppercase tracking-wider bg-gray-50">
                    <button
                      type="button"
                      onClick={() => handleSort('stock_factor')}
                      className="flex items-center gap-1 hover:text-gray-900 transition-colors ml-auto"
                      title="Доля бюджета/ставки: (остаток/(остаток+15))^1.35; 0 остаток → 0%"
                    >
                      <span>Коэф. остаток</span>
                      {sortColumn === 'stock_factor' && (
                        sortDirection === 'desc' ? (
                          <HiArrowDown className="w-4 h-4 text-blue-600" />
                        ) : (
                          <HiArrowUp className="w-4 h-4 text-blue-600" />
                        )
                      )}
                    </button>
                  </th>
                  <th className="text-center p-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">Действия</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {/* Итого */}
                <tr className="bg-gradient-to-r from-gray-50 to-gray-100 font-semibold">
                  <td colSpan="6" className="p-4 text-sm text-gray-700">
                    Итого: {formatBudget(totalWeeklyBudget)} в неделю
                  </td>
                  <td className="p-4 text-sm text-right text-gray-900">{formatNumber(totalSpend)}</td>
                  <td className="p-4 text-sm text-right text-gray-900">{formatNumber(totalViews)}</td>
                  <td className="p-4 text-sm text-right text-gray-900">{formatNumber(totalClicks)}</td>
                  <td className="p-4 text-sm text-right text-gray-900">{totalCtr.toFixed(2)}%</td>
                  <td className="p-4 text-sm text-right text-gray-900">{formatNumber(totalOrders)}</td>
                  <td className="p-4 text-sm text-right text-gray-900">{totalCpo ? formatNumber(totalCpo) : '—'}</td>
                  <td className="p-4 text-sm text-right text-gray-900">{formatNumber(totalRevenue)}</td>
                  <td className="p-4 text-sm text-right text-gray-900">{totalDrr !== null ? formatNumber(totalDrr) : '—'}</td>
                  <td className="p-4 text-sm"></td>
                  <td className="p-4 text-sm"></td>
                  <td className="p-4 text-sm"></td>
                </tr>
                
                {/* Кампании */}
                {sortedCampaigns.map((campaign, index) => {
                  const campaignCtr = campaign.views > 0 ? (campaign.clicks / campaign.views) * 100 : 0;
                  const stockFactor = computeStockResourceFactor(campaign.stock_count);
                  return (
                    <tr key={campaign.advertId} className="hover:bg-gray-50 transition-colors">
                      <td className="p-4 text-sm">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={selectedCampaignIds.has(campaign.advertId)}
                            onChange={(e) => {
                              const newSet = new Set(selectedCampaignIds);
                              if (e.target.checked) {
                                newSet.add(campaign.advertId);
                              } else {
                                newSet.delete(campaign.advertId);
                              }
                              setSelectedCampaignIds(newSet);
                            }}
                            className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                          />
                          <span className="text-gray-600">{index + 1}</span>
                        </div>
                      </td>
                      <td className="p-4 text-sm text-gray-600">
                        {typeMap[String(campaign.type)] || 'Неизвестно'}
                      </td>
                      <td className="p-4 text-sm">
                        <div className="font-medium text-gray-900">{campaign.name}</div>
                        <div className="text-xs text-gray-500 mt-1 flex gap-2">
                          <button
                            onClick={() => {
                              setSelectedCampaign(campaign);
                              setModalOpen(true);
                            }}
                            className="text-purple-600 hover:text-purple-700 font-medium"
                          >
                            Стратегия
                          </button>
                          <span className="text-gray-300">|</span>
                          <button
                            onClick={() => {
                              setSelectedCampaignForSchedule(campaign);
                              setScheduleModalOpen(true);
                            }}
                            className="text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1"
                          >
                            <HiClock className="w-3 h-3" />
                            Режим показов
                          </button>
                        </div>
                      </td>
                      <td className="p-4 text-sm">
                        {getStatusBadge(campaign.status)}
                      </td>
                      <td className="p-4 text-sm">
                        {campaign.daily_spend_limit ? (
                          <div className="font-medium text-blue-600">{formatNumber(campaign.daily_spend_limit)} ₽/день</div>
                        ) : (
                          <div className="text-gray-400 text-xs">Лимит не установлен</div>
                        )}
                        <div className="text-xs text-gray-500 mt-1">{getStrategyLabel(campaign)}</div>
                      </td>
                      <td className="p-4 text-sm text-gray-600">Поиск</td>
                      <td className="p-4 text-sm text-right font-medium text-gray-900">{formatNumber(campaign.spend)}</td>
                      <td className="p-4 text-sm text-right text-gray-700">{formatNumber(campaign.views)}</td>
                      <td className="p-4 text-sm text-right text-gray-700">{formatNumber(campaign.clicks)}</td>
                      <td className="p-4 text-sm text-right">
                        <span className={`font-medium ${campaignCtr > 5 ? 'text-green-600' : campaignCtr > 2 ? 'text-yellow-600' : 'text-red-600'}`}>
                          {campaignCtr.toFixed(2)}%
                        </span>
                      </td>
                      <td className="p-4 text-sm text-right text-gray-700">{formatNumber(campaign.orders)}</td>
                      <td className="p-4 text-sm text-right text-gray-700">{campaign.cpo ? formatNumber(campaign.cpo) : '—'}</td>
                      <td className="p-4 text-sm text-right font-medium text-gray-900">{formatNumber(campaign.revenue)}</td>
                      <td className="p-4 text-sm text-right">
                        {campaign.drr !== null ? (
                          <span className={`font-medium ${campaign.drr < 50 ? 'text-green-600' : campaign.drr < 100 ? 'text-yellow-600' : 'text-red-600'}`}>
                            {formatNumber(campaign.drr)}%
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="p-4 text-sm text-right text-gray-700">
                        {campaign.stock_count != null
                          ? Number(campaign.stock_count).toLocaleString('ru-RU')
                          : '—'}
                      </td>
                      <td className="p-4 text-sm text-right">
                        <span
                          className={`font-medium ${
                            stockFactor === 0
                              ? 'text-red-600'
                              : stockFactor < 0.15
                              ? 'text-orange-600'
                              : stockFactor < 0.5
                              ? 'text-yellow-700'
                              : 'text-green-700'
                          }`}
                          title="Вес в распределении бюджета и множитель ставки биддера"
                        >
                          {formatStockFactorPercent(stockFactor)}
                        </span>
                      </td>
                      <td className="p-4 text-sm">
                        <div className="flex items-center justify-center gap-2">
                          {campaign.status === 9 ? (
                            <button
                              onClick={() => handleCampaignAction(campaign.advertId, 'pause')}
                              disabled={actionLoading[campaign.advertId]}
                              className="text-orange-600 hover:text-orange-700 p-1.5 rounded hover:bg-orange-50 transition-colors"
                              title="Пауза"
                            >
                              <HiPause className="w-5 h-5" />
                            </button>
                          ) : campaign.status === 11 ? (
                            <button
                              onClick={() => handleCampaignAction(campaign.advertId, 'start')}
                              disabled={actionLoading[campaign.advertId]}
                              className="text-green-600 hover:text-green-700 p-1.5 rounded hover:bg-green-50 transition-colors"
                              title="Запустить"
                            >
                              <HiPlay className="w-5 h-5" />
                            </button>
                          ) : null}
                          {(campaign.status === 9 || campaign.status === 11 || campaign.status === 4) && (
                            <button
                              onClick={() => {
                                if (!window.confirm('Завершить кампанию? Это необратимо.')) return;
                                handleCampaignAction(campaign.advertId, 'stop');
                              }}
                              disabled={actionLoading[campaign.advertId]}
                              className="text-gray-600 hover:text-red-700 p-1.5 rounded hover:bg-red-50 transition-colors"
                              title="Завершить"
                            >
                              <HiStop className="w-5 h-5" />
                            </button>
                          )}
                          <button
                            onClick={() => handleCampaignAction(campaign.advertId, 'delete')}
                            disabled={actionLoading[campaign.advertId]}
                            className="text-red-600 hover:text-red-700 p-1.5 rounded hover:bg-red-50 transition-colors"
                            title="Удалить"
                          >
                            <HiTrash className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modalOpen && (
        <StrategyModal
          campaign={selectedCampaign}
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          onSave={saveCampaignStrategy}
          clientId={clientId}
        />
      )}

      {budgetModalOpen && (
        <MonthlyBudgetModal
          isOpen={budgetModalOpen}
          onClose={() => setBudgetModalOpen(false)}
          clientId={clientId}
          onUpdate={refreshDataInBackground}
        />
      )}

      {/* Модальное окно режима показов */}
      <ScheduleModal
        campaign={selectedCampaignForSchedule}
        isOpen={scheduleModalOpen}
        onClose={() => {
          setScheduleModalOpen(false);
          setSelectedCampaignForSchedule(null);
        }}
        clientId={clientId}
      />

      {/* Панель массового редактирования */}
      {selectedCampaignIds.size > 0 && (
        <BulkEditPanel
          selectedCampaignIds={Array.from(selectedCampaignIds)}
          campaigns={campaigns}
          onClose={() => setSelectedCampaignIds(new Set())}
          onUpdate={() => {
            refreshDataInBackground();
            setSelectedCampaignIds(new Set());
          }}
          clientId={clientId}
        />
      )}
    </div>
  );
}

// Компонент панели массового редактирования
function BulkEditPanel({ selectedCampaignIds, campaigns, onClose, onUpdate, clientId }) {
  const { notify, confirm } = useNotification();
  const [action, setAction] = useState(''); // 'start', 'pause', 'strategy', 'schedule'
  const [loading, setLoading] = useState(false);
  const [heatmapLoading, setHeatmapLoading] = useState(false);
  const [scheduleMessage, setScheduleMessage] = useState('');
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [strategyModalOpen, setStrategyModalOpen] = useState(false);
  const [schedule, setSchedule] = useState({});
  const days = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

  // Инициализация расписания
  useEffect(() => {
    setSchedule(emptyScheduleGrid());
  }, []);

  const selectedCampaigns = campaigns.filter(c => selectedCampaignIds.includes(c.advertId));

  const handleBulkAction = async (actionType) => {
    if (actionType === 'start' || actionType === 'pause' || actionType === 'stop') {
      if (actionType === 'stop') {
        const ok = window.confirm(
          `Завершить выбранные кампании (${selectedCampaignIds.length})? Это необратимо.`,
        );
        if (!ok) return;
      }
      setLoading(true);
      try {
        const path = actionType === 'start' ? 'start' : actionType === 'pause' ? 'pause' : 'stop';
        const promises = selectedCampaignIds.map((id) =>
          apiClient.post(`/campaigns/${id}/${path}`, {
            client_id: clientId,
          }),
        );
        await Promise.all(promises);
        onUpdate();
        setAction('');
      } catch (error) {
        console.error('Ошибка массового действия:', error);
        notify('Ошибка при выполнении действия', 'error');
      } finally {
        setLoading(false);
      }
    } else if (actionType === 'strategy') {
      setStrategyModalOpen(true);
    } else if (actionType === 'schedule') {
      setAction('schedule');
      setSchedule(emptyScheduleGrid());
      setScheduleMessage('');
      setScheduleModalOpen(true);
    }
  };

  const applyBulkHeatmapAction = async (mode) => {
    if (!clientId || selectedCampaignIds.length === 0) return;
    const count = selectedCampaignIds.length;

    if (mode === 'clear') {
      const ok = await confirm(`Очистить режим показов для ${count} кампаний? Все часы будут выключены и изменения сохранятся.`);
      if (!ok) return;
    }
    if (mode === 'auto') {
      const ok = await confirm({
        message:
          `Применить автоматический режим для ${count} кампаний?\n\nУ каждой кампании расписание строится по своей тепловой карте и цели биддера (CPO, CPC или ДРР). Кампании без цели будут пропущены.`,
      });
      if (!ok) return;
    }

    setHeatmapLoading(true);
    setScheduleMessage('');
    try {
      if (mode === 'clear') {
        await Promise.all(
          selectedCampaignIds.map((id) =>
            apiClient.post('/schedule/clear', {
              advert_id: Number(id),
              client_id: Number(clientId),
            })
          )
        );
        setSchedule(emptyScheduleGrid());
        setScheduleMessage(`Режим показов очищен для ${count} кампаний`);
      } else if (mode === 'auto') {
        const results = await Promise.allSettled(
          selectedCampaignIds.map((id) =>
            apiClient.post('/schedule/from-heatmap', {
              advert_id: Number(id),
              client_id: Number(clientId),
              mode: 'auto',
              days: 30,
              apply: true,
            })
          )
        );
        const ok = results.filter((r) => r.status === 'fulfilled').length;
        const fail = results.length - ok;
        const firstError = results
          .filter((r) => r.status === 'rejected')
          .map((r) => r.reason?.response?.data?.message)
          .find(Boolean);

        if (fail === 0) {
          setScheduleMessage(`Автоматический режим применён для ${ok} кампаний`);
          setTimeout(() => onUpdate(), 1500);
        } else if (ok === 0) {
          setScheduleMessage(firstError || 'Не удалось применить автоматический режим ни для одной кампании');
        } else {
          setScheduleMessage(
            `Применено: ${ok} из ${count}. Ошибок: ${fail}.${firstError ? ` ${firstError}` : ''}`
          );
        }
      } else {
        const res = await apiClient.post('/schedule/from-heatmap', {
          advert_id: Number(selectedCampaignIds[0]),
          client_id: Number(clientId),
          mode: 'average',
          days: 30,
        });
        setSchedule(normalizeScheduleData(res.data?.schedule || {}));
        setScheduleMessage(
          res.data?.explanation || 'Средние по аккаунту подставлены — нажмите «Сохранить для всех»'
        );
      }
    } catch (err) {
      setScheduleMessage(err.response?.data?.message || 'Ошибка при построении расписания');
      console.error('Ошибка bulk heatmap schedule:', err);
    } finally {
      setHeatmapLoading(false);
      setTimeout(() => setScheduleMessage(''), 8000);
    }
  };

  const handleSaveSchedule = async () => {
    setLoading(true);
    try {
      // Убеждаемся, что все 168 ячеек (7 дней * 24 часа) присутствуют в schedule
      const completeSchedule = {};
      for (let d = 0; d < 7; d++) {
        for (let h = 0; h < 24; h++) {
          const key = `${d}_${h}`;
          completeSchedule[key] = schedule[key] !== undefined ? schedule[key] : 0;
        }
      }

      const promises = selectedCampaignIds.map(id => {
        return apiClient.post('/schedule', {
          advert_id: Number(id),
          schedule: completeSchedule,
          client_id: Number(clientId)
        });
      });
      await Promise.all(promises);
      onUpdate();
      setAction('');
      setScheduleModalOpen(false);
    } catch (error) {
      console.error('Ошибка сохранения расписания:', error);
      const apiMsg = error.response?.data?.message;
      setScheduleMessage(apiMsg || 'Ошибка при сохранении расписания');
      setTimeout(() => setScheduleMessage(''), 8000);
    } finally {
      setLoading(false);
    }
  };

  const toggleCell = (day, hour) => {
    const key = `${day}_${hour}`;
    setSchedule(prev => ({
      ...prev,
      [key]: prev[key] === 100 ? 0 : 100,
    }));
  };

  const toggleDay = (dayIndex) => {
    const updated = { ...schedule };
    const allActive = Array.from({ length: 24 }, (_, h) => `${dayIndex}_${h}`).every(k => updated[k] === 100);
    const newValue = allActive ? 0 : 100;
    for (let h = 0; h < 24; h++) {
      const key = `${dayIndex}_${h}`;
      updated[key] = newValue;
    }
    setSchedule(updated);
  };

  const toggleHour = (hourIndex) => {
    const updated = { ...schedule };
    const allActive = Array.from({ length: 7 }, (_, d) => `${d}_${hourIndex}`).every(k => updated[k] === 100);
    const newValue = allActive ? 0 : 100;
    for (let d = 0; d < 7; d++) {
      const key = `${d}_${hourIndex}`;
      updated[key] = newValue;
    }
    setSchedule(updated);
  };

  if (selectedCampaignIds.length === 0) return null;

  return (
    <>
      {/* Панель снизу экрана */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-40">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-gray-700">
                Выбрано кампаний: <span className="text-purple-600 font-semibold">{selectedCampaignIds.length}</span>
              </span>
              {(
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleBulkAction('start')}
                    disabled={loading}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium disabled:opacity-50"
                  >
                    Включить
                  </button>
                  <button
                    onClick={() => handleBulkAction('pause')}
                    disabled={loading}
                    className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm font-medium disabled:opacity-50"
                  >
                    Отключить
                  </button>
                  <button
                    onClick={() => handleBulkAction('stop')}
                    disabled={loading}
                    className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium disabled:opacity-50"
                  >
                    Завершить
                  </button>
                  <button
                    onClick={() => handleBulkAction('strategy')}
                    disabled={loading}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium disabled:opacity-50"
                  >
                    Сменить стратегию
                  </button>
                  <button
                    onClick={() => handleBulkAction('schedule')}
                    disabled={loading}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-1"
                  >
                    <HiClock className="w-4 h-4" />
                    Режим показов
                  </button>
                </div>
              )}
            </div>
            <button
              onClick={() => {
                setAction('');
                onClose();
              }}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <HiX className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Модальное окно стратегии для массового редактирования */}
      <BulkStrategyModal
        campaignIds={selectedCampaignIds}
        isOpen={strategyModalOpen}
        onClose={() => {
          setStrategyModalOpen(false);
          setAction('');
        }}
        onSave={async (settings) => {
          const params = clientId ? { client_id: clientId } : {};
          await apiClient.put(`/bidder/campaign/${settings.campaign_id}/settings`, settings, { params });
        }}
        onUpdate={onUpdate}
        clientId={clientId}
      />

      {/* Модальное окно расписания для массового редактирования */}
      {scheduleModalOpen && (
        <ModalOverlay
          onClick={() => {
            setScheduleModalOpen(false);
            setAction('');
          }}
        >
          <div
            className="modal-panel rounded-2xl max-w-5xl w-full max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-header flex justify-between items-center p-4 sticky top-0 z-10 rounded-t-2xl">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Режим показов (массовое редактирование)</h3>
                <p className="text-xs text-gray-500 mt-0.5">Выбрано кампаний: {selectedCampaignIds.length}</p>
              </div>
              <button
                onClick={() => {
                  setScheduleModalOpen(false);
                  setAction('');
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <HiX className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              <div className="mb-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => applyBulkHeatmapAction('auto')}
                  disabled={heatmapLoading || loading}
                  className="glass-btn px-3 py-2 text-xs font-medium disabled:opacity-50"
                  title="Для каждой кампании — своё расписание по тепловой карте и цели биддера"
                >
                  {heatmapLoading ? '…' : 'Автоматический режим'}
                </button>
                <button
                  type="button"
                  onClick={() => applyBulkHeatmapAction('average')}
                  disabled={heatmapLoading || loading}
                  className="glass-btn-secondary px-3 py-2 text-xs font-medium disabled:opacity-50"
                  title="Одна сетка по средним данным аккаунта — сохранится для всех выбранных кампаний"
                >
                  Средние по аккаунту
                </button>
                <button
                  type="button"
                  onClick={() => applyBulkHeatmapAction('clear')}
                  disabled={heatmapLoading || loading}
                  className="px-3 py-2 text-xs font-medium rounded-lg border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-50"
                  title="Выключить все часы и сохранить для всех выбранных кампаний"
                >
                  Очистить
                </button>
              </div>

              <div className="overflow-x-auto mb-4 glass-card p-4">
                <div className="inline-block min-w-full">
                  <div className="flex gap-1">
                    <div className="w-12 flex-shrink-0"></div>
                    {Array.from({ length: 24 }, (_, h) => {
                      const allActive = Array.from({ length: 7 }, (_, d) => schedule[`${d}_${h}`] === 100).every(v => v);
                      return (
                        <div
                          key={h}
                          className="w-8 h-8 flex items-center justify-center bg-gray-700 text-white text-xs font-medium rounded cursor-pointer hover:bg-gray-600 transition-colors"
                          onClick={() => toggleHour(h)}
                        >
                          {h}
                        </div>
                      );
                    })}
                  </div>
                  {days.map((day, i) => {
                    const allDayActive = Array.from({ length: 24 }, (_, h) => schedule[`${i}_${h}`] === 100).every(v => v);
                    return (
                      <div key={i} className="flex gap-1 mt-1">
                        <div
                          className="w-12 h-8 flex items-center justify-center bg-gray-700 text-white text-xs font-medium rounded cursor-pointer hover:bg-gray-600 transition-colors flex-shrink-0"
                          onClick={() => toggleDay(i)}
                        >
                          {day}
                        </div>
                        {Array.from({ length: 24 }, (_, h) => {
                          const key = `${i}_${h}`;
                          const isActive = schedule[key] === 100;
                          return (
                            <div
                              key={h}
                              onClick={() => toggleCell(i, h)}
                              className={`w-8 h-8 flex items-center justify-center rounded cursor-pointer transition-all ${
                                isActive 
                                  ? 'bg-green-600 hover:bg-green-700 border border-green-700' 
                                  : 'bg-gray-200 hover:bg-gray-300 border border-gray-300'
                              }`}
                            >
                              {isActive && <HiCheck className="w-4 h-4 text-white" />}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>

              {scheduleMessage && (
                <div className={`mb-3 p-2 rounded text-center text-xs ${
                  scheduleMessage.includes('Ошибка')
                    || scheduleMessage.includes('Задайте')
                    || scheduleMessage.includes('Не удалось')
                    ? 'bg-red-50 text-red-700'
                    : 'bg-green-50 text-green-700'
                }`}>
                  {scheduleMessage}
                </div>
              )}

              <div className="flex justify-end gap-2">
                <button
                  onClick={() => {
                    setScheduleModalOpen(false);
                    setAction('');
                  }}
                  className="glass-btn-secondary px-4 py-2 text-sm font-medium"
                  disabled={heatmapLoading || loading}
                >
                  Отмена
                </button>
                <button
                  onClick={handleSaveSchedule}
                  disabled={loading || heatmapLoading}
                  className="glass-btn px-4 py-2 text-sm font-medium disabled:opacity-50"
                >
                  {loading ? 'Сохранение...' : 'Сохранить для всех'}
                </button>
              </div>
            </div>
          </div>
        </ModalOverlay>
      )}
    </>
  );
}

export {
  StrategyModal,
  BulkStrategyModal,
  MonthlyBudgetModal,
  ScheduleModal,
  BulkEditPanel,
  statusMap,
  typeMap,
};
